import { useRef, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import EditIcon from '@mui/icons-material/Edit';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import { Box, IconButton, Typography, Stack, Button } from '@mui/material';
import ProjectPriceDialog from './ProjectPriceDialog';
import dayjs from 'dayjs';
import { formatCurrency } from '../../utils/currencyHelper';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import ProjectAllPriceDialog from './ProjectAllPriceDialog';
import useParam from '../../context/ParamContext';

const EditToolbar = (props) => {
  const { config } = useConfig();
  const { disabled, push, defProjectPrice, setAllPriceDialogOpen } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defProjectPrice,
      currencyCode: config?.currencyCode ?? null,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `projectPrice.${values.projectPrice.length ?? 0}.itemCode`,
      `projectPrice.${values.projectPrice.length ?? 0}.direction`,
      `projectPrice.${values.projectPrice.length ?? 0}.currencyCode`,
      `projectPrice.${values.projectPrice.length ?? 0}.description`,
      `projectPrice.${values.projectPrice.length ?? 0}.qty`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
      <Button size="small" onClick={() => setAllPriceDialogOpen(true)} sx={{ ml: 'auto' }}>
        Projekt árak áttekintése
      </Button>
    </GridToolbarContainer>
  );
};

const ProjectPriceTable = ({
  disabled,
  defProjectPrice,
  autoCompleteOptions,
  setRefetchTable,
  setSelectedValues,
  selectedValues,
  autocompleteData,
}) => {
  const { params } = useParam();
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const defPriceDialogData = {
    open: false,
    currencyCode: '',
    itemCode: '',
    itemName: '',
    projectId: null,
    direction: '',
    priceRounding: 0,
  };
  const [priceDialogData, setPriceDialogData] = useState(defPriceDialogData);
  const [allPriceDialogOpen, setAllPriceDialogOpen] = useState(false);

  const getValueIndex = (values, id) => {
    return values.projectPrice.findIndex((rc) => rc.id === id);
  };

  const today = dayjs().startOf('day');

  const isPriceValidToday = (row) => {
    const from = dayjs(row.validFrom).startOf('day');
    const to = row.validTo ? dayjs(row.validTo).endOf('day') : null;

    if (to) {
      return today.isSame(from) || (today.isAfter(from) && today.isBefore(to)) || today.isSame(to);
    }

    return today.isSame(from) || today.isAfter(from);
  };

  const useQuantityPricing = params.PROJECT_PRICE_USE_QUANTITY === 'Y';

  const columns = (remove) =>
    dataGridPremiumColumns([
      {
        field: 'itemCode',
        headerName: useQuantityPricing ? 'Megnevezés' : 'Tevékenység',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autoCompleteOptions.item.find(
            (u) => u.value === values.projectPrice?.[valueIndex]?.itemCode
          );
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autoCompleteOptions.item.find(
            (u) => u.value === values.projectPrice?.[valueIndex]?.itemCode
          );
          return (
            <Field
              name={`projectPrice.${valueIndex}.itemCode`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPrice?.[valueIndex]?.itemCode} />}
              onChange={(_e, newVal) => {
                const val = newVal?.value ?? null;
                const oldItemCode = values.projectPrice[valueIndex].itemCode;
                const findedRow = values.projectPrice.find((pp) => pp.itemCode === val);

                if (val && findedRow) {
                  addNotification({
                    content: `A ${val} tevékenység már megtalálható a sorokban!`,
                    type: NotificationType.ERROR,
                  });
                  return;
                }

                for (let i = 0; i < values.projectPrice.length; i++) {
                  if (values.projectPrice[i].itemCode === oldItemCode) {
                    setFieldValue(`projectPrice.${i}.itemCode`, val);
                  }
                }
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
              }}
              onBlur={async () => await validateField(`projectPrice.${valueIndex}.itemCode`)}
              value={findedItem}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={autoCompleteOptions.item}
            />
          );
        },
      },
      {
        field: 'price',
        headerName: useQuantityPricing ? 'Szerződött ár' : 'Szerződött óradíj',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          const itemCode = data.row.itemCode;
          const matchingPrices = values.projectPrice.filter((pp) => pp.itemCode === itemCode);

          if (matchingPrices.length === 0) return '';

          const validPrice = matchingPrices.find((pp) => isPriceValidToday(pp));

          if (!validPrice?.price) return <Typography sx={{ color: 'error.main' }}>Nincs érvényes ár!</Typography>;

          const findedCurrency = autocompleteData?.currency?.find((u) => u.code === data.row.currencyCode);

          return formatCurrency(validPrice.price, findedCurrency?.priceRounding ?? 0);
        },
      },
      {
        field: 'currencyCode',
        headerName: 'Pénznem',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autoCompleteOptions.currency.find(
            (u) => u.value === values.projectPrice?.[valueIndex]?.currencyCode
          );
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autoCompleteOptions.currency.find(
            (u) => u.value === values.projectPrice?.[valueIndex]?.currencyCode
          );
          return (
            <Field
              name={`projectPrice.${valueIndex}.currencyCode`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPrice?.[valueIndex]?.currencyCode} />}
              onChange={(_e, newVal) => {
                const val = newVal?.value ?? null;
                const oldItemCode = values.projectPrice[valueIndex].itemCode;

                for (let i = 0; i < values.projectPrice.length; i++) {
                  if (values.projectPrice[i].itemCode === oldItemCode) {
                    setFieldValue(`projectPrice.${i}.currencyCode`, val);
                  }
                }
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
              }}
              onBlur={async () => await validateField(`projectPrice.${valueIndex}.currencyCode`)}
              value={findedItem}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={autoCompleteOptions.currency}
            />
          );
        },
      },
      ...(useQuantityPricing
        ? [
            {
              field: 'qty',
              headerName: 'Mennyiség',
              type: 'number',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);

                return (
                  <Field
                    name={`projectPrice.${valueIndex}.qty`}
                    type="number"
                    as={InputUI}
                    variant="standard"
                    fullWidth
                    helperText={<FormErrorUI message={errors?.projectPrice?.[valueIndex]?.qty} />}
                    onChange={(e) => {
                      setFieldValue(`projectPrice.${valueIndex}.qty`, e.target.value);
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () => await validateField(`projectPrice.${valueIndex}.qty`)}
                  />
                );
              },
            },
            {
              field: 'quantityUnitCode',
              headerName: 'ME',
              type: 'string',
              sortable: false,
              renderCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);
                const itemCode = values.projectPrice?.[valueIndex]?.itemCode;

                if (!itemCode) return '';

                const quantityUnitId = autocompleteData?.item?.find((u) => u.itemCode === itemCode)?.quantityUnitId;

                const quantityUnitCode = autocompleteData?.qtyUnit?.find((u) => u.id === quantityUnitId)?.code;

                return quantityUnitCode ?? '';
              },
            },
          ]
        : []),
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPrice.${valueIndex}.description`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPrice?.[valueIndex]?.description} />}
              onChange={(e) => {
                const oldItemCode = values.projectPrice[valueIndex].itemCode;

                for (let i = 0; i < values.projectPrice.length; i++) {
                  if (values.projectPrice[i].itemCode === oldItemCode) {
                    setFieldValue(`projectPrice.${i}.description`, e.target.value);
                  }
                }
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target?.value,
                });
              }}
              onBlur={async () => await validateField(`projectPrice.${valueIndex}.description`)}
            />
          );
        },
      },
      {
        field: 'editPrice',
        headerName: 'Ár szerkesztése',
        type: 'actions',
        sortable: false,
        getActions: ({ row }) => {
          const findedItem = autoCompleteOptions.item.find((item) => item.value === row.itemCode);
          const findedCurrency = autocompleteData?.currency?.find((item) => item.code === row.currencyCode);

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              disabled={disabled || row.isNew}
              onClick={() =>
                setPriceDialogData({
                  open: true,
                  currencyCode: row.currencyCode,
                  itemCode: row.itemCode,
                  itemName: findedItem?.label,
                  projectId: row.projectId,
                  direction: row.direction,
                  priceRounding: findedCurrency?.priceRounding ?? 0,
                })
              }
              color="inherit"
            />,
          ];
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setFieldValue(
                  'projectPrice',
                  values.projectPrice.filter((pp) => pp.itemCode !== row.itemCode)
                );
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);

  const uniqueItemRows = useMemo(() => {
    if (!values?.projectPrice) return [];

    const seen = new Set();
    return values.projectPrice.filter((pp) => {
      if (seen.has(pp.itemCode)) return false;
      seen.add(pp.itemCode);
      return true;
    });
  }, [values.projectPrice]);

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="projectPrice">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.ProjectPriceTable}
                  height={480}
                  columns={columns(remove)}
                  rows={uniqueItemRows}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defProjectPrice,
                      setAllPriceDialogOpen,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled || params.field === 'editPrice') return;

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>

      <ProjectPriceDialog
        data={priceDialogData}
        onClose={() => setPriceDialogData(defPriceDialogData)}
        setRefetchTable={setRefetchTable}
        setSelectedValues={setSelectedValues}
      />
      <ProjectAllPriceDialog
        open={allPriceDialogOpen}
        onClose={() => setAllPriceDialogOpen(false)}
        selectedValues={selectedValues}
      />
    </>
  );
};

export default ProjectPriceTable;
